import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Checkbox,
  Button,
  DatePicker,
  Radio,
  Alert,
} from 'antd';
import moment from 'moment';

const { MonthPicker } = DatePicker;

const MAX_MONTHS = 24;
const INTER_MONTH_DELAY_SEC = 5;
const lastMonth = () => moment().subtract(1, 'month');

const buildMonthsArray = (from, to) => {
  const months = [];
  const cursor = from.clone().startOf('month');
  const end = to.clone().startOf('month');
  while (cursor.isSameOrBefore(end, 'month')) {
    months.push(cursor.format('YYYY-MM'));
    cursor.add(1, 'month');
  }
  return months;
};

const parseHubIds = hubIdsValue =>
  hubIdsValue
    ? hubIdsValue
        .split(',')
        .map(id => parseInt(id.trim(), 10))
        .filter(Boolean)
    : [];

const TriggerModal = ({
  onSave,
  onCancel,
  form,
  triggerLoading,
  queueMode = false,
}) => {
  const { getFieldDecorator, getFieldValue, resetFields } = form;
  const [mode, setMode] = useState('single');

  const rangeFrom = getFieldValue('rangeFrom');
  const rangeTo = getFieldValue('rangeTo');

  const getRangeMonthCount = () => {
    if (!rangeFrom || !rangeTo) return 0;
    return buildMonthsArray(rangeFrom, rangeTo).length;
  };

  const rangeMonthCount = getRangeMonthCount();
  const rangeExceedsMax = rangeMonthCount > MAX_MONTHS;

  const disabledMonth = current =>
    current && current.isAfter(lastMonth(), 'month');

  const handleSave = e => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (err) return;

      let months;

      if (mode === 'single') {
        const m = values.monthKey || lastMonth();
        months = [m.format('YYYY-MM')];
      } else {
        if (!values.rangeFrom || !values.rangeTo) return;
        months = buildMonthsArray(values.rangeFrom, values.rangeTo);
        if (months.length > MAX_MONTHS) return;
      }

      onSave({
        params: {
          months,
          force: values.force || false,
          hubIds: parseHubIds(values.hubIds),
          ...(queueMode ? { queueOnly: true } : {}),
        },
      });
    });
  };

  const handleModeChange = e => {
    setMode(e.target.value);
    if (e.target.value === 'single') {
      resetFields(['rangeFrom', 'rangeTo']);
    } else {
      resetFields(['monthKey']);
    }
  };

  return (
    <Modal
      title={
        queueMode
          ? 'Add Engagement Case Report Job to Queue'
          : 'Trigger Engagement Case Report Cron'
      }
      visible
      width={620}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
        <Button
          key="run"
          type="primary"
          onClick={handleSave}
          loading={triggerLoading}
          disabled={mode === 'range' && rangeExceedsMax}
        >
          {queueMode ? 'Add to queue' : 'Run'}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        {queueMode && (
          <Alert
            type="info"
            showIcon
            message="This job will be added to the worker queue and picked up when the worker is ready. You can add as many jobs as you want."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form.Item label="Run mode">
          <Radio.Group value={mode} onChange={handleModeChange}>
            <Radio.Button value="single">Single month</Radio.Button>
            <Radio.Button value="range">Month range</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {mode === 'single' ? (
          <Form.Item label="Month">
            {getFieldDecorator('monthKey', {
              initialValue: lastMonth(),
            })(
              <MonthPicker
                format="YYYY-MM"
                placeholder="Select month"
                disabledDate={disabledMonth}
                style={{ width: '100%' }}
              />
            )}
          </Form.Item>
        ) : (
          <>
            <Form.Item label="From month">
              {getFieldDecorator('rangeFrom', {
                rules: [{ required: true, message: 'From month is required' }],
              })(
                <MonthPicker
                  format="YYYY-MM"
                  placeholder="From"
                  disabledDate={disabledMonth}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
            <Form.Item label="To month">
              {getFieldDecorator('rangeTo', {
                rules: [{ required: true, message: 'To month is required' }],
              })(
                <MonthPicker
                  format="YYYY-MM"
                  placeholder="To"
                  disabledDate={disabledMonth}
                  style={{ width: '100%' }}
                />
              )}
            </Form.Item>
            {rangeMonthCount > 0 && (
              <Form.Item>
                {rangeExceedsMax ? (
                  <Alert
                    type="error"
                    message={`Range covers ${rangeMonthCount} months — maximum is ${MAX_MONTHS}. Please narrow the range.`}
                    showIcon
                  />
                ) : (
                  <Alert
                    type="info"
                    message={`${rangeMonthCount} month${rangeMonthCount > 1 ? 's' : ''} selected. ~${rangeMonthCount} min estimated (${INTER_MONTH_DELAY_SEC} sec gap between months). Hubs for each month run in parallel.`}
                    showIcon
                  />
                )}
              </Form.Item>
            )}
          </>
        )}

        <Form.Item label="Hub IDs (optional, comma-separated)">
          {getFieldDecorator('hubIds')(
            <Input placeholder="e.g. 39,903 — leave blank to run for all hubs" />
          )}
        </Form.Item>

        <Form.Item>
          {getFieldDecorator('force', {
            valuePropName: 'checked',
            initialValue: false,
          })(
            <Checkbox>
              Force (bypass dedup — reprocess already-processed hubs)
            </Checkbox>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Form.create({ name: 'engagement_case_report_trigger_modal' })(
  TriggerModal
);
